import { useEffect, useState, useRef } from "react";
import { Button } from "../components/ui/button";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

/**
 * Lightweight product-tour overlay.
 *
 * Each step targets a CSS selector. The matched element gets a glowing ring +
 * a vignette darkens the rest of the page. A floating tooltip-card explains
 * what the user is looking at, with Back / Next / Skip controls.
 *
 * Persistence: when the tour finishes (or skipped), `storageKey` is set in
 * localStorage so it never auto-runs again. The user can still re-run via
 * `<button onClick={() => { localStorage.removeItem(key); reload }} />`.
 */
export default function ProductTour({ steps, storageKey, autostart = true, onClose }) {
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const targetRef = useRef(null);

  useEffect(() => {
    if (!autostart) return;
    if (typeof window === "undefined") return;
    // One-time migration: tours used to live under `bankable_tour_*` keys.
    // Bridge any existing "seen" state to the new `cfograde_tour_*` key so
    // users who already dismissed the tour don't see it again post-rename.
    const legacyKey = storageKey.replace("cfograde_tour_", "bankable_tour_");
    if (legacyKey !== storageKey && localStorage.getItem(legacyKey)) {
      const existing = localStorage.getItem(storageKey);
      if (!existing) {
        localStorage.setItem(storageKey, localStorage.getItem(legacyKey));
      }
      localStorage.removeItem(legacyKey);
    }
    if (localStorage.getItem(storageKey)) return;
    // Delay slightly so the page settles
    const t = setTimeout(() => setActive(true), 600);
    return () => clearTimeout(t);
  }, [autostart, storageKey]);

  // Apply spotlight class to current step's target
  useEffect(() => {
    if (!active) return;
    const step = steps[idx];
    if (!step) return;
    const el = step.selector ? document.querySelector(step.selector) : null;
    targetRef.current = el;
    if (!el) return;
    el.classList.add("tour-spotlight", "tour-pulse");
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    return () => {
      el.classList.remove("tour-spotlight", "tour-pulse");
    };
  }, [active, idx, steps]);

  function close() {
    localStorage.setItem(storageKey, JSON.stringify({ done_at: new Date().toISOString() }));
    setActive(false);
    onClose?.();
  }

  if (!active) return null;
  const step = steps[idx];
  if (!step) return null;
  const isLast = idx === steps.length - 1;

  return (
    <>
      {/* Vignette layer — pointer-events:none so target stays clickable */}
      <div className="fixed inset-0 z-[55] pointer-events-none no-print" data-testid="tour-overlay" />

      {/* Tooltip card — fixed at bottom on mobile, bottom-right on desktop */}
      <div
        className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[70] bg-white border border-[#E5E0D8] rounded-2xl shadow-2xl p-5 fade-up no-print"
        role="dialog"
        aria-label="Product tour"
        data-testid="tour-card"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={14} strokeWidth={1.5} className="text-[#C26D5C]" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#5C665D]">
              Quick tour · {idx + 1} of {steps.length}
            </span>
          </div>
          <button
            onClick={close}
            className="text-[#5C665D] hover:text-[#1F382E] transition-colors"
            aria-label="Close tour"
            data-testid="tour-close-btn"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <h3 className="font-serif-display text-xl text-[#1F382E] leading-tight">
          {step.title}
        </h3>
        <p className="text-sm text-[#1A1A1A] leading-relaxed mt-2">{step.body}</p>

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={close}
            className="text-xs text-[#5C665D] hover:text-[#1F382E] transition-colors"
            data-testid="tour-skip-btn"
          >
            Skip
          </button>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <Button
                variant="ghost"
                onClick={() => setIdx((i) => i - 1)}
                className="text-[#5C665D] hover:bg-[#F7F5F0] h-9 px-3"
                data-testid="tour-back-btn"
              >
                <ArrowLeft size={14} strokeWidth={1.5} className="mr-1" />
                Back
              </Button>
            )}
            <Button
              onClick={() => (isLast ? close() : setIdx((i) => i + 1))}
              className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] h-9 px-4 transition-all duration-300"
              data-testid="tour-next-btn"
            >
              {isLast ? "Got it" : "Next"}
              {!isLast && <ArrowRight size={14} strokeWidth={1.5} className="ml-1.5" />}
            </Button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-1.5">
          {steps.map((_, i) => (
            <span
              key={i}
              className="h-1 rounded-full transition-all duration-300"
              style={{
                width: i === idx ? 24 : 8,
                background: i <= idx ? "#1F382E" : "#E5E0D8",
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export const ANALYZE_TOUR_KEY = "cfograde_tour_analyze_v1";
export const REPORT_TOUR_KEY = "cfograde_tour_report_v1";

export const ANALYZE_TOUR_STEPS = [
  {
    selector: '[data-testid="audience-toggle"]',
    title: "Pick your reading lens.",
    body: "Owner / Operator gives you a margin-improvement playbook. Analyst / Lender writes a credit memo with covenants and risk triggers. Same numbers, very different memo.",
  },
  {
    selector: '[data-testid="field-industry"]',
    title: "Tell us your industry.",
    body: "We grade your ratios against typical operators in that sector. Bakery, SaaS, construction, healthcare — all 16 industries are dialled in.",
  },
  {
    selector: '[data-testid="field-region"]',
    title: "Pick a region.",
    body: "Defaults to the one matching your currency. The US has its own tightened benchmark overlay; UK, EU, India, Brazil and others have selective overrides where defensible data exists.",
  },
  {
    selector: '[data-testid="load-sample-btn"]',
    title: "No numbers yet? One click.",
    body: "Load Sample fills the form with a small US bakery so you can see exactly what comes out the other side before entering anything sensitive.",
  },
  {
    selector: '[data-testid="run-analysis-btn"]',
    title: "Run analysis when you're ready.",
    body: "Computes 24+ ratios, your CFO Grade, peer benchmark and (if you've got AI credit) a tailored memo. About 5 seconds.",
  },
];

export const REPORT_TOUR_STEPS = [
  {
    selector: '[data-testid="health-rating-badge"]',
    title: "This is your CFO Grade.",
    body: "0–100, weighted across profitability, liquidity, solvency and efficiency. Anything 80+ is bankable territory; below 45 needs attention soon.",
  },
  {
    selector: '[data-testid="benchmark-card"]',
    title: "How you stack up.",
    body: "Every key ratio compared to the typical range for your industry and region. Toggle Global ↔ In-region in the corner to switch frames.",
  },
  {
    selector: '[data-testid="whatif-card"]',
    title: "Test changes live.",
    body: "Drag any slider — raise prices 3%, cut inventory 20 days, refinance — and watch your CFO Grade recompute. People stay on this for 20 minutes.",
  },
  {
    selector: '[data-testid="share-score-btn"]',
    title: "Share it.",
    body: "Generates a clean 1200×630 PNG with your score and rating — perfect for LinkedIn or a board deck. Or hit Print / PDF for a full report.",
  },
];
